import React, { useState, useEffect, useRef } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import feedbackService from "../../services/feedbackService";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  RefreshCcw,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Tag,
  TriangleAlert,
  Check,
  X,
  ChevronDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

// ─── Tag config (General first, then domain-specific) ────────────────────────
const TAG_OPTIONS = [
  { value: "general",          label: "General",          cls: "bg-gray-100 text-gray-600 border-gray-200"      },
  { value: "medical",          label: "Medical",          cls: "bg-blue-50 text-blue-700 border-blue-200"       },
  { value: "code",             label: "Code",             cls: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "image_generation", label: "Image Generation", cls: "bg-green-100 text-green-700 border-green-200"   },
  { value: "file_generation",  label: "File Generation",  cls: "bg-amber-50 text-amber-700 border-amber-200"    },
];

const getTagCfg = (tag) =>
  TAG_OPTIONS.find((o) => o.value === tag) || { label: tag, cls: "bg-gray-100 text-gray-600 border-gray-200" };

const TagBadge = ({ tag }) => {
  const { label, cls } = getTagCfg(tag);
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${cls}`}>
      <Tag className="h-3 w-3" />
      {label}
    </span>
  );
};

// ─── Styled tag select ────────────────────────────────────────────────────────
const TagSelect = ({ value, onChange, error }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = getTagCfg(value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-3 h-9 text-sm border rounded-lg bg-white transition-colors
          ${open ? "border-[#2B3674] ring-1 ring-[#2B3674]/20" : error ? "border-red-400 bg-red-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"}`}
      >
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${selected.cls}`}>
          <Tag className="h-3 w-3" />{selected.label}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 shrink-0 ml-2 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-xl py-1 overflow-hidden">
          {TAG_OPTIONS.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
                value === opt.value ? "bg-blue-50/60" : "hover:bg-gray-50"
              }`}
            >
              <div className={`h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                value === opt.value ? "bg-[#2B3674] border-[#2B3674]" : "border-gray-300 bg-white"
              }`}>
                {value === opt.value && (
                  <svg className="h-2.5 w-2.5 text-white" fill="none" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${opt.cls}`}>
                <Tag className="h-3 w-3" />{opt.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Form field wrapper ───────────────────────────────────────────────────────
const Field = ({ label, error, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
    {children}
    {hint && !error && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
  </div>
);

const inputCls = (hasError) =>
  `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B3674]/20 focus:border-[#2B3674] transition-colors ${
    hasError ? "border-red-400 bg-red-50" : "border-gray-200"
  }`;

// ─── Main Component ───────────────────────────────────────────────────────────
const CategoryTable = () => {
  const {
    categories, setCategories,
    categoriesLoading, setCategoriesLoading,
    categoriesError, setCategoriesError,
    canManageCategories,
    refreshTrigger,
    triggerRefresh,
    clearFeedbackSelection,
  } = useFeedback();

  const [showFormModal, setShowFormModal]     = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deletingCategoryId, setDeletingCategoryId] = useState(null);
  const [categoryToDelete, setCategoryToDelete]     = useState(null);
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [reordering, setReordering]           = useState(false);
  const [formErrors, setFormErrors]           = useState({});
  // Categories are fetched all at once (no cursor/limit from the API), so
  // pagination here is just client-side slicing of the already-sorted list —
  // same Total / Rows per page / page-arrows layout as Prompt Management.
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const emptyForm = { name: "", display_name: "", description: "", tag: "general" };
  const [formData, setFormData] = useState(emptyForm);

  // ─── Data fetching ──────────────────────────────────────────────────────────
  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      const result = await feedbackService.getAllFeedbackCategories();
      setCategories(result.categories || []);
    } catch {
      setCategoriesError("Failed to load categories");
      toast.error("Failed to load categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (canManageCategories) fetchCategories();
  }, [refreshTrigger, canManageCategories]);

  // ─── Sorted list ────────────────────────────────────────────────────────────
  const sorted = [...(categories || [])].sort(
    (a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name)
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / limit));
  const pageSafe = Math.min(page, totalPages);
  const paged = sorted.slice((pageSafe - 1) * limit, pageSafe * limit);

  // ─── Reorder: move up / move down ──────────────────────────────────────────
  const moveCategory = async (index, direction) => {
    const newList = [...sorted];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newList.length) return;

    // Swap display_order values
    const aOrder = newList[index].display_order;
    const bOrder = newList[swapIndex].display_order;

    // If orders are equal (common edge case), assign distinct ones
    const newAOrder = bOrder === aOrder ? bOrder - (direction === "up" ? 1 : -1) : bOrder;
    const newBOrder = aOrder;

    const updated = newList.map((cat, i) => {
      if (i === index)     return { ...cat, display_order: newAOrder };
      if (i === swapIndex) return { ...cat, display_order: newBOrder };
      return cat;
    });

    // Optimistic update
    setCategories(updated);

    // Persist both changes
    // NOTE: requires PATCH /admin/feedback/categories/{id} to accept display_order
    try {
      setReordering(true);
      await Promise.all([
        feedbackService.updateFeedbackCategory(newList[index].id, { ...newList[index], display_order: newAOrder }),
        feedbackService.updateFeedbackCategory(newList[swapIndex].id, { ...newList[swapIndex], display_order: newBOrder }),
      ]);
    } catch {
      toast.error("Failed to save order — please refresh");
      fetchCategories(); // Revert on failure
    } finally {
      setReordering(false);
    }
  };

  // ─── Form handling ──────────────────────────────────────────────────────────
  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "display_name") {
        next.name = value.toLowerCase().replace(/[^a-z0-9\s]/g, "").replace(/\s+/g, "_").trim();
      }
      return next;
    });
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    else if (!/^[a-z0-9_]+$/.test(formData.name)) errors.name = "Lowercase letters, numbers, underscores only";
    if (!formData.display_name.trim()) errors.display_name = "Display name is required";
    if (!formData.description.trim()) errors.description = "Description is required";
    if (!formData.tag) errors.tag = "Tag is required";
    const isDuplicate = categories.some((c) => c.name === formData.name && c.id !== editingCategory?.id);
    if (isDuplicate) errors.name = "A category with this name already exists";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      const maxOrder = sorted.length > 0 ? Math.max(...sorted.map((c) => c.display_order)) : 0;
      const payload = editingCategory
        ? formData
        : { ...formData, display_order: maxOrder + 10 }; // New categories go to the end

      if (editingCategory) {
        await feedbackService.updateFeedbackCategory(editingCategory.id, payload);
        toast.success("Category updated successfully");
      } else {
        await feedbackService.createFeedbackCategory(payload);
        toast.success("Category created — it's been added to the end of the list");
      }
      fetchCategories();
      closeFormModal();
    } catch {
      toast.error(editingCategory ? "Failed to update category" : "Failed to create category");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setEditingCategory(null);
    setShowFormModal(true);
  };

  const openEditModal = (category) => {
    setFormData({ name: category.name, display_name: category.display_name, description: category.description, tag: category.tag });
    setFormErrors({});
    setEditingCategory(category);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingCategory(null);
    setFormData(emptyForm);
    setFormErrors({});
  };

  // ─── Delete handling ────────────────────────────────────────────────────────
  const handleDeleteCategory = (category) => setCategoryToDelete(category);
  const closeDeleteModal = () => { if (!deletingCategoryId) setCategoryToDelete(null); };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;
    try {
      setDeletingCategoryId(categoryToDelete.id);
      await feedbackService.deleteFeedbackCategory(categoryToDelete.id);
      toast.success(`"${categoryToDelete.display_name}" deleted`);
      setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
      setCategoryToDelete(null);
    } catch (err) {
      if (err.response?.status === 404) toast.error("Category not found");
      else if (err.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to delete category");
    } finally {
      setDeletingCategoryId(null);
    }
  };

  if (!canManageCategories) return null;

  return (
    <div className="flex flex-col h-full min-h-0 gap-2">

      <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
      {/* Header (inside the card, as in the design) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0 px-5 py-4">
        <div className="flex w-full items-start justify-between gap-3 sm:w-auto">
          <div>
            <h3 className="text-base font-bold text-[#14122B]">Feedback Categories</h3>
            <p className="text-xs text-[#9AA1B2] mt-0.5">
              Classify feedback · use the arrows to reorder
            </p>
          </div>
          {/* Phones: refresh icon at the top right of the table (the top Refresh button is hidden on this tab) */}
          <button
            type="button"
            onClick={() => {
              clearFeedbackSelection();
              triggerRefresh();
            }}
            disabled={categoriesLoading}
            title="Refresh"
            aria-label="Refresh"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-white text-slate-700 shadow-sm hover:border-gray-300 transition-colors disabled:opacity-60 sm:hidden"
          >
            <RefreshCcw className={`h-4 w-4 ${categoriesLoading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={openCreateModal}
            size="sm"
            className="bg-[#2B3674] hover:bg-[#1a2456] text-white gap-1.5 transition-all duration-150"
          >
            <Plus className="h-4 w-4" />
            Add Category
          </Button>
        </div>
      </div>

      {/* Table */}
        <div className="table-scroll flex-1 min-h-0" style={{ overflowY: "auto", overflowX: "auto" }}>
          <Table className="border-collapse w-full min-w-[1100px] xl:min-w-[900px]" style={{ tableLayout: "fixed" }}>
            <TableHeader>
              <TableRow className="border-b border-gray-100 hover:bg-indigo-50">
                {[
                  { label: "Order",        width: "90px"  },
                  { label: "Display Name", width: "190px" },
                  { label: "Internal Name",width: "190px" },
                  { label: "Description",  width: "340px" },
                  { label: "Tag",          width: "160px" },
                  { label: "",             width: "48px"  },
                ].map(({ label, width }) => (
                  <TableHead
                    key={label || "actions"}
                    style={{
                      position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20,
                      verticalAlign: "middle", width, minWidth: width, textAlign: "left",
                    }}
                    className="text-xs font-semibold text-gray-500 uppercase tracking-wide py-3.5 px-4"
                  >
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {categoriesLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <RefreshCcw className="h-5 w-5 animate-spin" />
                      <span className="text-sm">Loading categories…</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : categoriesError ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-red-500">
                      <TriangleAlert className="h-6 w-6" />
                      <span className="text-sm">{categoriesError}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <Tag className="h-5 w-5" />
                      <span className="text-sm">No categories yet</span>
                      <button onClick={openCreateModal} className="text-xs text-[#2B3674] hover:underline cursor-pointer">
                        Add your first category
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((category, localIndex) => {
                  const index = (pageSafe - 1) * limit + localIndex;
                  return (
                  <TableRow
                    key={category.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50/80 transition-colors"
                  >
                    {/* Order controls */}
                    <TableCell
                      className="px-3"
                      style={{ verticalAlign: "middle", width: "90px" }}
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-400 tabular-nums w-5 text-center">{index + 1}</span>
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveCategory(index, "up")}
                            disabled={index === 0 || reordering}
                            className="h-5 w-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                            title="Move up"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => moveCategory(index, "down")}
                            disabled={index === sorted.length - 1 || reordering}
                            className="h-5 w-5 flex items-center justify-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-200 disabled:opacity-20 disabled:cursor-not-allowed transition-colors"
                            title="Move down"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </TableCell>

                    {/* Display Name */}
                    <TableCell className="px-4" style={{ verticalAlign: "middle" }}>
                      <span className="text-sm font-semibold text-gray-900">{category.display_name}</span>
                    </TableCell>

                    {/* Internal Name */}
                    <TableCell className="px-4" style={{ verticalAlign: "middle" }}>
                      <code className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-md font-mono">
                        {category.name}
                      </code>
                    </TableCell>

                    {/* Description */}
                    <TableCell className="px-4 overflow-hidden" style={{ verticalAlign: "middle", maxWidth: 0 }}>
                      <span className="text-sm text-gray-600 truncate block" title={category.description}>
                        {category.description}
                      </span>
                    </TableCell>

                    {/* Tag */}
                    <TableCell className="px-4" style={{ verticalAlign: "middle" }}>
                      <TagBadge tag={category.tag} />
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="px-2" style={{ verticalAlign: "middle" }}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={deletingCategoryId === category.id}
                            className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-xl rounded-lg min-w-[160px] p-1">
                          <DropdownMenuItem
                            onClick={() => openEditModal(category)}
                            className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-gray-700 hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors"
                          >
                            <Pencil className="h-4 w-4 shrink-0" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-gray-100 my-1" />
                          <DropdownMenuItem
                            onClick={() => handleDeleteCategory(category)}
                            disabled={deletingCategoryId === category.id}
                            className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-red-600 hover:bg-red-50 focus:bg-red-50 outline-none transition-colors"
                          >
                            {deletingCategoryId === category.id
                              ? <><RefreshCcw className="h-4 w-4 animate-spin shrink-0" />Deleting…</>
                              : <><Trash2 className="h-4 w-4 shrink-0" />Delete</>
                            }
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* ── Pagination (same layout as Prompt Management) ──────────────────── */}
      <div className="mt-auto pt-2 pb-2 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm shrink-0">
        <span>Total {sorted.length}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button disabled={pageSafe <= 1} onClick={() => setPage(pageSafe - 1)} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl">‹</button>

            <span>
              {sorted.length === 0 ? "0 / 0" : `${pageSafe} / ${totalPages}`}
            </span>

            <button disabled={pageSafe >= totalPages} onClick={() => setPage(pageSafe + 1)} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl">›</button>
          </div>
        </div>
      </div>
      </div>

      {/* ── Create / Edit Modal ─────────────────────────────────────────────── */}
      {showFormModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) closeFormModal(); }}
        >
          <div className="w-full max-w-lg m-4">
            <Card className="shadow-2xl bg-white">
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Tag className="h-5 w-5 text-[#2B3674]" />
                    <h2 className="text-lg font-semibold text-gray-900">
                      {editingCategory ? "Edit Category" : "New Category"}
                    </h2>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={closeFormModal}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Display Name *" error={formErrors.display_name}>
                      <input
                        placeholder="User-friendly name"
                        value={formData.display_name}
                        onChange={(e) => handleFormChange("display_name", e.target.value)}
                        className={inputCls(!!formErrors.display_name)}
                      />
                    </Field>
                    <Field
                      label="Internal Name *"
                      error={formErrors.name}
                      hint="Auto-generated from display name, can be edited but must be unique and contain only [a-z0-9_]"
                    >
                      <input
                        placeholder="snake_case_name"
                        value={formData.name}
                        onChange={(e) => handleFormChange("name", e.target.value)}
                        className={inputCls(!!formErrors.name)}
                      />
                    </Field>
                  </div>

                  <Field label="Description *" error={formErrors.description}>
                    <textarea
                      placeholder="Describe when users should select this category"
                      value={formData.description}
                      onChange={(e) => handleFormChange("description", e.target.value)}
                      rows={3}
                      className={inputCls(!!formErrors.description) + " resize-none"}
                    />
                  </Field>

                  <Field label="Tag *" error={formErrors.tag}>
                    <TagSelect
                      value={formData.tag}
                      onChange={(v) => handleFormChange("tag", v)}
                      error={formErrors.tag}
                    />
                  </Field>

                  {!editingCategory && (
                    <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                      New categories are added to the end of the list. Use the ↑↓ arrows in the table to reposition.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
                  <Button variant="outline" onClick={closeFormModal} className="text-gray-600 hover:bg-gray-100">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-[#2B3674] hover:bg-[#1E2951] text-white gap-1.5"
                  >
                    {isSubmitting
                      ? <><RefreshCcw className="h-4 w-4 animate-spin" />{editingCategory ? "Updating…" : "Creating…"}</>
                      : <><Check className="h-4 w-4" />{editingCategory ? "Update Category" : "Create Category"}</>
                    }
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ───────────────────────────────────────── */}
      {categoryToDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => { if (e.target === e.currentTarget) closeDeleteModal(); }}
        >
          <div className="w-full max-w-md m-4">
            <Card className="shadow-2xl bg-white">
              <div className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-red-500" />
                    <h2 className="text-lg font-semibold text-gray-900">Delete Category</h2>
                  </div>
                  <Button
                    variant="ghost" size="sm"
                    onClick={closeDeleteModal}
                    disabled={!!deletingCategoryId}
                    className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <p className="text-sm text-gray-700 mb-4">
                  Are you sure you want to delete{" "}
                  <span className="font-semibold text-gray-900">"{categoryToDelete.display_name}"</span>?
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5">
                  <div className="flex gap-2">
                    <TriangleAlert className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800 space-y-1">
                      <p className="font-semibold">Important</p>
                      <p>This will hide the category from user feedback forms.</p>
                      <p>Existing feedback using this category will be preserved.</p>
                      <p>This action cannot be easily undone.</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                  <Button variant="outline" onClick={closeDeleteModal} disabled={!!deletingCategoryId} className="text-gray-600 hover:bg-gray-100">
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmDelete}
                    disabled={!!deletingCategoryId}
                    className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
                  >
                    {deletingCategoryId
                      ? <><RefreshCcw className="h-4 w-4 animate-spin" />Deleting…</>
                      : <><Trash2 className="h-4 w-4" />Delete Category</>
                    }
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryTable;